import { model as router } from '!/router'
import { F, isEmpty, T, toIsoBirthDate } from '@/helpers'
import {
  birthDate,
  email,
  isDatePassed,
  isDateTooOld,
  maxLength,
  minLength,
  password,
  required,
  whenDateComplete,
} from '@/validators'
import { api } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEvent,
  createStore,
  sample,
  split,
  type EventCallable,
  type StoreWritable,
} from 'effector'
import { createForm } from 'effector-forms'
import { condition } from 'patronum'
import { model as session } from '~/entities/session'
import {
  LINKEDIN_CONVERSION_ID_FOR_SIGN_UP_SUBMIT_BTN,
  tracking,
} from '~/entities/tracking'
import { getLandingRegionUrlFromAlias } from '~/features/landingRegion'
import { preferencesFeatureModel } from '~/features/preferences'
import { SIGN_UP_ERROR_CODE } from '~/shared/constants/errorCode'
import { ERROR } from '~/shared/constants/errors'
import { PATH } from '~/shared/constants/path'
import { seoTrackingSignUpFx } from './seoTracking'

function homePathAfterSignUp(alias: string | undefined): string {
  const landingRegion = alias ? getLandingRegionUrlFromAlias(alias) : ''
  return `${PATH.HOME}${landingRegion}`
}

// init page
export const init: EventCallable<{ query: URLSearchParams }> = createEvent()

export const waitFx = session.checkStatusFx

// sign up effect
export const signUpFx = attach({ effect: api.user.signUpFx })

// sign up form
export const signUpForm = createForm({
  fields: {
    email: {
      init: '' as string,
      rules: [required(), email(), maxLength(50)],
    },
    password: {
      init: '' as string,
      rules: [required(), minLength(6), maxLength(64), password()],
    },
    firstName: {
      init: '' as string,
      rules: [required(), maxLength(80)],
    },
    // Birth date is optional here, so a date the user started and did not
    // finish is ignored rather than rejected: `toIsoBirthDate` omits it and
    // registration goes through (OVP-3074).
    birthday: {
      init: '' as string,
      rules: [
        whenDateComplete(birthDate()),
        whenDateComplete(isDatePassed()),
        whenDateComplete(isDateTooOld()),
      ],
    },
    gender: {
      init: '' as string,
    },
  },
  validateOn: ['submit', 'blur'],
})

export const $canSubmit = combine(
  signUpForm.fields.email.$value,
  signUpForm.fields.password.$value,
  signUpForm.fields.firstName.$value,
  (email, password, firstName) =>
    !isEmpty(email) && !isEmpty(password) && !isEmpty(firstName)
)

// send sign up request
// `marketingConsent` is sent implicitly, the form has no checkbox for it (OVP-2954)
sample({
  clock: signUpForm.formValidated,
  fn: ({ email, password, firstName, birthday, gender }) => ({
    email: email.trim(),
    password,
    firstName: firstName.trim(),
    birthDate: toIsoBirthDate(birthday),
    gender: gender || undefined,
    marketingConsent: true,
  }),
  target: signUpFx,
})

export const resetErrorOnClick: EventCallable<void> = createEvent()

// Only an already registered address gets its own message. Every other email
// failure — syntax, length, dead domain, inconclusive MX lookup — reads the
// same, and no raw backend code ever reaches the user (OVP-2974).
function signUpErrorMessage(errorCode: string | undefined): string {
  switch (errorCode) {
    case SIGN_UP_ERROR_CODE.EMAIL_ALREADY_EXISTS:
      return ERROR.SIGN_UP_EMAIL_ALREADY_EXISTS
    case SIGN_UP_ERROR_CODE.EMAIL_INVALID_FORMAT:
    case SIGN_UP_ERROR_CODE.EMAIL_TOO_LONG:
    case SIGN_UP_ERROR_CODE.EMAIL_DOMAIN_UNDELIVERABLE:
    case SIGN_UP_ERROR_CODE.EMAIL_LOOKUP_UNAVAILABLE:
      return ERROR.SIGN_UP_EMAIL_INVALID
    default:
      return ERROR.SOMETHING_WENT_WRONG
  }
}

// error to show to the user
export const $error: StoreWritable<string | null> = createStore<string | null>(
  null
)
  .on(signUpFx.failData, (_, error) =>
    signUpErrorMessage(error?.payload?.errorCode)
  )
  .reset(init, resetErrorOnClick)

// Only the sign-up form itself. `/sign-up/verify` is a child of this path, and
// since sign-up opens a session (OVP-3045) the verification link is normally
// opened in the browser that just registered — a substring match sent it to
// Home before the token was ever activated (OVP-3078).
// `/sign-up/` renders the same form, so the trailing slash has to go first.
const $isSignUpPage = router.$pathname.map(
  (pathname) => pathname?.replace(/\/$/, '') === PATH.SIGN_UP
)

// The success toast belongs to the Home page the user lands on, and the toast
// model hides itself on every pathname change, so the Home route raises it on
// its own init and clears this latch.
export const accountCreatedShown: EventCallable<void> = createEvent()

export const $accountCreated: StoreWritable<boolean> = createStore<boolean>(
  false
)
  .on(signUpFx.done, T)
  .reset(accountCreatedShown, init)

signUpFx.done.watch(() =>
  tracking.lintrk(LINKEDIN_CONVERSION_ID_FOR_SIGN_UP_SUBMIT_BTN)
)

export const openSignUpPopup: EventCallable<void> = createEvent()
export const closeSignUpPopup: EventCallable<void> = createEvent()

export const checkEnableToClose = createEvent()

sample({
  clock: signUpFx.done,
  target: seoTrackingSignUpFx,
})

// The popup stays up until Home has been handed to React. Closing on
// signUpFx.done uncovers the page underneath while the Home chunk is still
// loading (OVP-3067).
const armSignUpPopupHold = createEvent()
const releaseSignUpPopup = createEvent()
const finishSignUpImmediately = createEvent()

// Navigation calls this once the next route has been passed to React.
export const routeRendered: EventCallable<void> = createEvent()

// ...and this when that route could not be resolved at all. Without it a chunk
// that fails to load would leave the popup up for good — the error page it
// falls back to can fail to load as well, and then nothing ever renders.
export const routeFailed: EventCallable<void> = createEvent()

export const $isSignUpPopupHeld: StoreWritable<boolean> = createStore(false)
  .on(armSignUpPopupHold, T)
  .reset(releaseSignUpPopup)

condition({
  source: checkEnableToClose, //FIXME: fix typescript error
  if: combine(
    $error,
    $isSignUpPopupHeld,
    (error, isHeld) => Boolean(error) || isHeld
  ),
  then: resetErrorOnClick,
  else: closeSignUpPopup,
})

sample({
  clock: [init, openSignUpPopup],
  target: signUpForm.reset,
})

export const $isOpenedSignUpPopup: StoreWritable<boolean> =
  createStore<boolean>(false) //
    .on(openSignUpPopup, T)
    .on(closeSignUpPopup, F)

// Sign up creates the account and the session in one request, so the user goes
// straight to Home — no verification step and no second sign in (OVP-3045).
// On the sign-up page itself the redirect below, clocked on the session, already
// does it; pushing twice would leave a duplicate history entry.
// Where sign-up sends the user and whether the popup has to wait for that page
// are one decision: the popup may only be held for a push that really happens,
// otherwise nothing would ever release it (OVP-3067).
const signUpSucceeded = sample({
  clock: signUpFx.done,
  source: {
    isSignUpPage: $isSignUpPage,
    isOpened: $isOpenedSignUpPopup,
    pathname: router.$pathname,
    preference: preferencesFeatureModel.$preference,
  },
  fn: ({ isSignUpPage, isOpened, pathname, preference }) => {
    const home = homePathAfterSignUp(preference?.alias)
    const navigate = !isSignUpPage
    return { home, navigate, hold: navigate && isOpened && pathname !== home }
  },
})

// armed before the push, so the release can never arrive first
split({
  source: signUpSucceeded,
  match: {
    hold: ({ hold }) => hold,
  },
  cases: {
    hold: armSignUpPopupHold,
    __: finishSignUpImmediately,
  },
})

sample({
  clock: signUpSucceeded,
  filter: ({ navigate }) => navigate,
  fn: ({ home }) => home,
  target: router.navigatePush,
})

sample({
  clock: finishSignUpImmediately,
  target: [closeSignUpPopup, signUpForm.reset],
})

sample({
  clock: [routeRendered, routeFailed],
  filter: $isSignUpPopupHeld,
  target: releaseSignUpPopup,
})

sample({
  clock: releaseSignUpPopup,
  target: [closeSignUpPopup, signUpForm.reset],
})

export const $enableSocialAuth: StoreWritable<boolean> =
  createStore<boolean>(true)

// parse query params to detect 'withSocialAuth' param to be able to ignore social authentication for mobile platforms
sample({
  clock: init,
  filter: ({ query }) => query.has('withSocialAuth'),
  fn: ({ query }) => {
    const withSocialAuth = query.get('withSocialAuth')
    return withSocialAuth ? JSON.parse(withSocialAuth) : true
  },
  target: $enableSocialAuth,
})

sample({
  clock: [init, session.$isAuthenticated],
  source: {
    isAuthenticated: session.$isAuthenticated,
    isSignUpPage: $isSignUpPage,
    preference: preferencesFeatureModel.$preference,
  },
  filter: ({ isAuthenticated, isSignUpPage }) =>
    Boolean(isAuthenticated && isSignUpPage),
  fn: ({ preference }) => homePathAfterSignUp(preference?.alias),
  target: router.navigatePush,
})
